import Axios from 'axios'
import { GameSceneManifestJson } from '../../API/GameSceneJson'
import { validateGameScene } from '../../API/validation'
import Config from '../../Containers/Config'
import GameSceneContainer from '../../Containers/GameSceneContainer/GameSceneContainer'
import InfoLayerContainer from '../../Containers/InfoLayerContainer/InfoLayerContainer'
import InventoryItemDetailsBox from '../../Containers/InventoryItemDetailsBox/InventoryItemDetailsBox'
import LabelContainer from '../../Containers/LabelContainer/LabelContainer'
import SubtitlesContainer from '../../Containers/SubtitlesContainer/SubtitlesContainer'
import AudioController from '../../Controllers/AudioController'
import InventoryCombinationsController from '../../Controllers/InventoryCombinationsController'
import LoadingController from '../../Controllers/LoadingController'
import dispatch from '../../Utility/dispatchAction'
import Image from '../Image'
import InfoLayer from '../InfoLayer'
import InteractableObject from '../InteractableObject'
import InteractiveLayer from '../InteractiveLayer/InteractiveLayer'
import TakeableObject from '../TakeableObject'
import Video from '../Video/Video'
import Waypoint from '../Waypoint'

export enum GameSceneActionType {
    enter = 'GameScene.enter',
    leave = 'GameScene.leave',
}

const createGameSceneAction = (type: GameSceneActionType, gameScene: GameScene) => ({
    type,
    payload: {
        gameScene,
    },
})

export const createGameSceneActionListener = (
    id: string,
    type: GameSceneActionType,
    callback?: (gameScene: GameScene) => void,
    once = false
): Promise<GameScene> =>
    new Promise<GameScene>((resolve) => {
        const handler: EventListener = (event) => {
            // @ts-ignore -- WHY: CustomEvent
            const gameScene = event.detail.gameScene as GameScene

            if (gameScene.id === id && event.type === type) {
                once && removeEventListener(type, handler)
                callback && callback(gameScene)
                resolve(gameScene)
            }
        }

        addEventListener(type, handler)
    })

type GameSceneProps = {
    id: string
    element: HTMLDivElement
    loopVideo: Video
    ambienceAudioId?: string
    intro?: {
        text: string
        audioId: string
    }

    interactiveLayer: InteractiveLayer
    infoLayer: InfoLayer

    interactableObjects: Record<string, InteractableObject>
    takeableObjects: Record<string, TakeableObject>
    waypoints: Record<string, Waypoint>
}

export default class GameScene {
    id: string
    element: HTMLDivElement
    loopVideo: Video
    ambienceAudioId?: string
    intro?: {
        text: string
        audioId: string
        played: boolean
    }

    interactiveLayer: InteractiveLayer
    infoLayer: InfoLayer

    interactableObjects: Record<string, InteractableObject>
    takeableObjects: Record<string, TakeableObject>
    waypoints: Record<string, Waypoint>

    constructor(props: GameSceneProps) {
        this.id = props.id
        this.loopVideo = props.loopVideo
        this.ambienceAudioId = props.ambienceAudioId
        this.intro = props.intro && {
            ...props.intro,
            played: false,
        }
        this.infoLayer = props.infoLayer
        this.interactiveLayer = props.interactiveLayer
        this.interactableObjects = props.interactableObjects
        this.takeableObjects = props.takeableObjects
        this.waypoints = props.waypoints

        this.element = props.element

        this.hide()
        GameSceneContainer.addScene(this)
    }

    static loadGameSceneManifest = async (gameSceneId: string) => {
        const baseUrl = `${Config.GAME_SCENE_PATH}${gameSceneId}/`
        const getAudioUrl = (src: string) => `${baseUrl}audio/${src}${Config.AUDIO_FILE_EXT}`
        const getVideoUrl = (src: string) => `${baseUrl}video/${src}${Config.VIDEO_FILE_EXT}`

        const response = await Axios.get(`${baseUrl}${gameSceneId}.json`, {
            responseType: 'json',
        })

        const manifest: GameSceneManifestJson = response.data

        if (!validateGameScene) {
            throw new Error('Inamove::GameScene => could not get validation!')
        }

        if (!validateGameScene(manifest)) {
            console.error(
                `Inamove::GameScene => GameSceneManifest validation for "${gameSceneId}" failed:`,
                validateGameScene.errors
            )
            throw new Error()
        }

        const withCorrectUrls: GameSceneManifestJson = {
            ...manifest,
            ambience: manifest.ambience && getAudioUrl(manifest.ambience),
            intro: manifest.intro && {
                ...manifest.intro,
                srcURL: getAudioUrl(manifest.intro.srcURL),
            },
            loop: getVideoUrl(manifest.loop),
            interactableObjects: manifest.interactableObjects.map((interactableObject) => ({
                ...interactableObject,
                inspect: interactableObject.inspect && {
                    ...interactableObject.inspect,
                    srcURL: getAudioUrl(interactableObject.inspect.srcURL),
                },
                interact: interactableObject.interact && {
                    ...interactableObject.interact,
                    srcURL: getAudioUrl(interactableObject.interact.srcURL),
                },
            })),
            takeableObjects: manifest.takeableObjects.map((takeableObject) => ({
                ...takeableObject,
                inspect: takeableObject.inspect && {
                    ...takeableObject.inspect,
                    srcURL: getAudioUrl(takeableObject.inspect.srcURL),
                },
                interact: takeableObject.interact && {
                    ...takeableObject.interact,
                    srcURL: getAudioUrl(takeableObject.interact.srcURL),
                },
            })),
            waypoints: manifest.waypoints.map((waypoint) => ({
                ...waypoint,
                inspect: waypoint.inspect && {
                    ...waypoint.inspect,
                    srcURL: getAudioUrl(waypoint.inspect.srcURL),
                },
                interact: waypoint.interact && {
                    ...waypoint.interact,
                    srcURL: getAudioUrl(waypoint.interact.srcURL),
                },
                inspectLocked: waypoint.inspectLocked && {
                    ...waypoint.inspectLocked,
                    srcURL: getAudioUrl(waypoint.inspectLocked.srcURL),
                },
                interactLocked: waypoint.interactLocked && {
                    ...waypoint.interactLocked,
                    srcURL: getAudioUrl(waypoint.interactLocked.srcURL),
                },
            })),
        }

        return withCorrectUrls
    }

    static load = async (gameSceneId: string): Promise<GameScene> => {
        console.debug(`Inamove::GameScene => load ${gameSceneId}`)

        const baseUrl = `${Config.GAME_SCENE_PATH}${gameSceneId}/`
        const manifest = await GameScene.loadGameSceneManifest(gameSceneId)
        console.debug(`Inamove::GameScene => manifest loaded for ${gameSceneId}`)

        // create element
        const element = document.createElement('div')
        element.classList.add('game-scene')

        // load loop video
        const loopVideo = new Video(manifest.loop, { loop: true, muted: true })
        LoadingController.addToQueue(loopVideo)
        element.appendChild(loopVideo.videoElement)

        // load interactive layer
        const interactiveLayer = await InteractiveLayer.load(`${baseUrl}${gameSceneId}.svg`)

        // load info Layer
        const infoLayer = InfoLayer.fromInteractiveLayer(gameSceneId + '-info-layer', interactiveLayer)
        element.appendChild(interactiveLayer.element)
        console.debug(`Inamove::GameScene => InteractiveLayer loaded for ${gameSceneId}`)

        // load interactable objects
        const interactableObjects = manifest.interactableObjects.reduce(
            (acc, interactableObjectManifest) => ({
                ...acc,
                [interactableObjectManifest.id]: new InteractableObject(
                    interactableObjectManifest,
                    interactiveLayer,
                    infoLayer
                ),
            }),
            {}
        )
        console.debug(`Inamove::GameScene => InteractableObjects loaded for ${gameSceneId}`)

        // load takeable objects
        const takeableObjects = manifest.takeableObjects.reduce((acc, takeableObjectManifest) => {
            const takeableObject = new TakeableObject(
                takeableObjectManifest,
                interactiveLayer,
                infoLayer,
                element,
                manifest.id
            )

            LoadingController.addToQueue(takeableObject)

            return {
                ...acc,
                [takeableObjectManifest.id]: takeableObject,
            }
        }, {})

        await InventoryCombinationsController.loadCombinations(
            manifest.takeableObjects.map((takeableObjectManifest) => takeableObjectManifest.inventoryItemID)
        )

        console.debug(`Inamove::GameScene => TakeableObjects loaded for ${gameSceneId}`)

        // load waypoints
        const waypoints = manifest.waypoints.reduce(
            (acc, waypointManifest) => ({
                ...acc,
                [waypointManifest.id]: new Waypoint(waypointManifest, interactiveLayer, infoLayer),
            }),
            {}
        )
        console.debug(`Inamove::GameScene => Waypoints loaded for ${gameSceneId}`)

        const props: GameSceneProps = {
            id: manifest.id,
            element,
            loopVideo,
            ambienceAudioId: manifest.ambience && AudioController.add(manifest.ambience),
            intro: manifest.intro && {
                ...manifest.intro,
                audioId: AudioController.add(manifest.intro.srcURL),
            },
            infoLayer,
            interactiveLayer,
            interactableObjects,
            takeableObjects,
            waypoints,
        }
        console.debug(`Inamove::GameScene => ${gameSceneId} loaded`)

        return new GameScene(props)
    }

    show = () => {
        this.element.classList.remove('hidden')
    }

    hide = () => {
        this.element.classList.add('hidden')
    }

    addImage = (image: Image) => {
        image.element.classList.add('overlay-image')
        this.element.prepend(image.element)
    }

    removeImage = (image: Image, destroy = false) => {
        this.element.removeChild(image.element)

        if (destroy) {
            image.destroy()
        }
    }

    addVideo = (video: Video) => {
        video.videoElement.classList.add('overlay-video')
        this.element.prepend(video.videoElement)
    }

    removeVideo = (video: Video) => {
        this.element.removeChild(video.videoElement)
    }

    removeInteractableObject = (interactableObjectId: string) => {
        this.interactableObjects[interactableObjectId]?.destroy()
        this.infoLayer.removeHotspot(interactableObjectId)
    }

    hideInteractableObject = (interactableObjectId: string) => {
        this.interactableObjects[interactableObjectId]?.hide()
        this.infoLayer.hideHotspot(interactableObjectId)
    }

    showInteractableObject = (interactableObjectId: string) => {
        this.interactableObjects[interactableObjectId]?.show()
        this.infoLayer.showHotspot(interactableObjectId)
    }

    hideTakeableObject = (takeableObjectId: string) => {
        this.takeableObjects[takeableObjectId]?.hide()
        this.infoLayer.hideHotspot(takeableObjectId)
    }

    showTakeableObject = (takeableObjectId: string) => {
        this.takeableObjects[takeableObjectId]?.show()
        this.infoLayer.showHotspot(takeableObjectId)
    }

    hideWaypoint = (waypointId: string) => {
        this.waypoints[waypointId]?.hide()
        this.infoLayer.hideHotspot(waypointId)
    }

    showWaypoint = (waypointId: string) => {
        this.waypoints[waypointId]?.show()
        this.infoLayer.showHotspot(waypointId)
    }

    destroy = () => {
        GameSceneContainer.removeScene(this)
        this.intro && AudioController.remove(this.intro.audioId)
        this.ambienceAudioId && AudioController.remove(this.ambienceAudioId)
        this.loopVideo.destroy()
        this.interactiveLayer.destroy()
        // WHY don't delete infoLayer: info layer is handled by InfoLayerContainer
        // TODO destroy interactable objects
    }

    // TODO: maybe set up an 'GameScene.stop' action listener
    stop = async () => {
        // TODO: animate
        // await this.element.animate([{ opacity: 0 }], { duration: 1000 }).finished
        this.hide()
        AudioController.stopCurrentActionAudio()
        AudioController.stop(this.ambienceAudioId)
        InfoLayerContainer.unsetInfoLayer()
        this.loopVideo.pause()
        LabelContainer.clear()
        SubtitlesContainer.clear()
        InventoryItemDetailsBox.hide()

        dispatch(createGameSceneAction(GameSceneActionType.leave, this))
    }

    start = async () => {
        console.debug(`>>>>> GameScene start: ${this.id}`)
        // this.element.style.setProperty('opacity', '0')
        this.show()
        // await this.element.animate([{ opacity: 1 }], { duration: 1000 }).finished

        // TODO: Show GameScene loop still image in background to prevent blackScreen (possible?)
        this.loopVideo.play()
        this.ambienceAudioId && AudioController.loop(this.ambienceAudioId)
        InfoLayerContainer.setInfoLayer(this.infoLayer)

        if (this.intro && !this.intro.played) {
            AudioController.play(this.intro.audioId)
            this.intro.played = true
        }

        dispatch(createGameSceneAction(GameSceneActionType.enter, this))
    }
}
